import React, { useEffect, useReducer, useRef } from "react";
import ModalContainer from "ui/ModalContainer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import Button from "ui/Button";

import Tip from "ui/Tip";
import {
  GroupedHintList,
  HintList,
  extractPlannerHints,
  formatPlannerHints,
} from "./util";
import CreatableSelect from "ui/CreatableSelect";
import { useParams } from "react-router-dom";
import { useHintPlanDebugQuery } from "./gql/Query.generated";
import SQL from "ui/SQL";

export type PlannerHints = Hint[];
type Hint = {
  name: string;
  value: string;
};
type HintWithLabel = {
  name: string;
  label: string;
  value: string;
};
type HintOptionType = {
  label: string;
  value: string;
};

type PlannerHintsPanelState = {
  query: string;
  error?: string;
  hints: PlannerHints;
  selectedHint: HintWithLabel;
  needsValidation: boolean;
};

type PlannerHintsPanelAction =
  | {
      type: "error";
      error: string;
    }
  | {
      type: "remove";
      hintIdx: number;
    }
  | {
      type: "select";
      hint: HintWithLabel;
    }
  | {
      type: "update";
      value: string;
    }
  | {
      type: "submit";
    }
  | {
      type: "confirm";
    };

function plannerHintsPanelStateReducer(
  state: PlannerHintsPanelState,
  action: PlannerHintsPanelAction,
) {
  switch (action.type) {
    case "error":
      return {
        ...state,
        error: action.error,
        needsValidation: false,
      };
    case "remove": {
      const { hintIdx } = action;
      const remainingHints = state.hints.slice();
      remainingHints.splice(hintIdx, 1);
      return {
        ...state,
        hints: remainingHints,
        query: formatPlannerHints(state.query, remainingHints),
      };
    }
    case "select":
      return {
        ...state,
        selectedHint: action.hint,
        error: undefined,
      };
    case "update":
      return {
        ...state,
        selectedHint: {
          ...state.selectedHint,
          value: action.value,
        },
      };
    case "submit":
      // submit selected hint for validation
      if (state.selectedHint.value === "") {
        return {
          ...state,
          error: "Value is required",
        };
      }
      if (hintExists(state.hints, state.selectedHint)) {
        return {
          ...state,
          error: "This hint already exists",
        };
      }
      return {
        ...state,
        needsValidation: true,
      };
    case "confirm": {
      // confirm validation of the selected hint and add it to the plan hints
      const newHints = [...state.hints, state.selectedHint];
      return {
        hints: newHints,
        query: formatPlannerHints(state.query, newHints),
        selectedHint: {
          name: "",
          label: "",
          value: "",
        },
        error: "",
        needsValidation: false,
      };
    }

    default:
      return state;
  }
}

function usePlannerHintsPanelState({
  databaseId,
  workbookId,
  query,
  onQueryChange,
}: {
  databaseId: string;
  workbookId: string;
  query: string;
  onQueryChange: (query: string) => void;
}) {
  const [state, dispatch] = useReducer(plannerHintsPanelStateReducer, {
    query,
    hints: extractPlannerHints(query),
    selectedHint: {
      name: "",
      label: "",
      value: "",
    },
    needsValidation: false,
  });

  useEffect(() => {
    onQueryChange(state.query);
  }, [state.query, onQueryChange]);

  const allHints = [...state.hints];
  if (state.selectedHint.value) {
    allHints.push({
      name: state.selectedHint.name,
      value: state.selectedHint.value,
    });
  }

  const hintPlan = allHints.map((val) => val.name + val.value).join(" ");
  const { data } = useHintPlanDebugQuery({
    variables: {
      databaseId,
      workbookId,
      hintPlan,
      queryText: state.query,
    },
    skip: !state.needsValidation,
    onError: (error) => {
      dispatch({
        type: "error",
        error: error.message,
      });
    },
  });
  const debugInfo = data?.getQueryHintPlanDebug;
  useEffect(() => {
    if (!debugInfo || !state.needsValidation) {
      return;
    }

    const newHints = [
      ...state.hints,
      { name: state.selectedHint.name, value: state.selectedHint.value },
    ];
    const newHint = state.selectedHint.name + state.selectedHint.value;

    if (debugInfo.error === "no valid hint") {
      dispatch({
        type: "error",
        error: "No valid hint detected. Please try another hints.",
      });
      return;
    } else if (debugInfo.error) {
      dispatch({
        type: "error",
        error: `Error: ${debugInfo.error}`,
      });
      return;
    } else if (debugInfo.usedHints.some((hint) => hint === newHint)) {
      // A new hint is used
      const removedHints = newHints.filter(
        (val) =>
          !debugInfo.usedHints.some((hint) => hint === val.name + val.value),
      );
      if (removedHints.length > 0) {
        dispatch({
          type: "error",
          error: `This hint will remove the existing hints (${removedHints
            .map((val) => val.name + val.value)
            .join(
              " ",
            )}). Please remove the existing hint first if this is expected.`,
        });
        return;
      }
      // Otherwise, confirm the proposed change.
      dispatch({
        type: "confirm",
      });
    } else if (debugInfo.notUsedHints.some((hint) => hint === newHint)) {
      // Not used hints = incorrect object definitions
      dispatch({
        type: "error",
        error:
          "This hint will not be used. Please check if the object (table, index) name is correct.",
      });
    } else if (debugInfo.duplicationHints.some((hint) => hint === newHint)) {
      // Duplication hints = redundant hints are defined or hints conflict with each other
      dispatch({
        type: "error",
        error:
          "This hint will not be used. This is a redundant hint or conflicts with existing hints.",
      });
    } else if (debugInfo.errorHints.some((hint) => hint === newHint)) {
      // Error hints are usually syntax error
      dispatch({
        type: "error",
        error:
          "This hint contains a syntax error. Please check whether the hint is using the correct value types (e.g., integer vs. boolean) and that the number of values is appropriate (e.g., NestLoop requires at least two relations).",
      });
    }
  }, [state, debugInfo, dispatch]);

  return [state, dispatch] as const;
}

function hintExists(hints: PlannerHints, hint: HintWithLabel) {
  return hints.some((h) => h.name === hint.name && h.value === hint.value);
}

const PlannerHintsPanel = ({
  query,
  setQuery,
  onDismiss,
}: {
  query: string;
  setQuery: (query: string) => void;
  onDismiss: () => void;
}) => {
  const { databaseId, workbookId } = useParams();
  const [state, dispatch] = usePlannerHintsPanelState({
    databaseId,
    workbookId,
    query,
    onQueryChange: setQuery,
  });
  const errorMessage = state.error;
  // Unsupported query error means that the hint validation will never work with this query.
  // We still want to show the availability to save the hint bypassing validation.
  const showBypassValidation = errorMessage?.startsWith("Unsupported query");
  function setErrorMessage(msg: string) {
    dispatch({
      type: "error",
      error: msg,
    });
  }

  const selectedHint = state.selectedHint;

  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpdateHints = () => {
    dispatch({
      type: "submit",
    });
  };

  const handleRemoveHint = (index: number) => {
    dispatch({
      type: "remove",
      hintIdx: index,
    });
  };

  const handleHintSelected = (hintLabel: string) => {
    const hint = HintList.find((h) => h.label === hintLabel);
    if (hint) {
      dispatch({
        type: "select",
        hint: {
          name: hint.name,
          label: hint.label,
          value: "",
        },
      });
    } else {
      dispatch({
        type: "select",
        hint: {
          name: "",
          label: "",
          value: "",
        },
      });
    }
  };

  const selectedHintDetail = HintList.find(
    (h) => h.label === selectedHint.label,
  );
  const options = Object.entries(GroupedHintList).map(([group, hints]) => {
    return {
      label: group,
      options: hints.map((h) => ({
        value: h.label,
        label: h.label,
      })),
    };
  });

  return (
    <ModalContainer
      title="Edit Planner Hints"
      layout="right sidebar"
      onClose={onDismiss}
    >
      <div className="grid gap-2 mb-2">
        <div className="leading-7 font-medium">pg_hint_plan settings</div>
        <div className="grid gap-2">
          {state.hints.length > 0 ? (
            state.hints.map((hint, i) => {
              const hintDetail = HintList.find((h) => h.name === hint.name);
              return (
                <div key={`hint-plan-${i}`} className="flex">
                  <div className="grow">
                    <span className="font-mono">
                      {hint.name}
                      {hint.value}
                    </span>{" "}
                    <Tip content={hintDetail.description} />
                  </div>
                  <Button
                    bare
                    onClick={() => handleRemoveHint(i)}
                    className="ml-2"
                    variant="danger"
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </div>
              );
            })
          ) : (
            <div>No custom planner hints</div>
          )}
        </div>
      </div>
      <hr />
      <div className="grid gap-2">
        <div className="leading-7 font-medium">
          Select a pg_hint_plan hint to edit
        </div>
        <div className="flex form-group items-center justify-between !mb-0">
          <div className="flex items-center">
            <div className="w-[220px]">
              <CreatableSelect<HintOptionType>
                value={
                  selectedHint.name === ""
                    ? null
                    : {
                        label: selectedHintDetail.label ?? selectedHint.name,
                        value: selectedHint.name,
                      }
                }
                options={options}
                onChange={(newValue) => {
                  handleHintSelected(newValue.value);
                  // Delay focus a bit as a workaround (otherwise it won't focus to the input field properly)
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }}
              />
            </div>
            {selectedHint.name && (
              <>
                <div className="w-[220px] ml-3">
                  <input
                    className="form-control"
                    type="text"
                    ref={inputRef}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue != selectedHint.value) {
                        dispatch({
                          type: "update",
                          value: newValue,
                        });
                      }
                      if (!/^\([^()]*\)$/.test(newValue)) {
                        setErrorMessage(
                          "The value must be surrounded by parentheses",
                        );
                      } else {
                        setErrorMessage("");
                      }
                    }}
                    value={selectedHint.value}
                    placeholder={selectedHintDetail.syntaxExample}
                  />
                </div>
              </>
            )}
          </div>
          {selectedHint.name && (
            <div className="ml-2">
              <button
                className="btn btn-success"
                onClick={() => {
                  showBypassValidation
                    ? dispatch({
                        type: "confirm",
                      })
                    : handleUpdateHints();
                }}
                disabled={!!errorMessage && !showBypassValidation}
              >
                {showBypassValidation ? "Save without validation" : "Save hint"}
              </button>
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="text-[#C22426]">
            <FontAwesomeIcon icon={faExclamationCircle} />{" "}
            {showBypassValidation ? (
              <>
                Unable to validate hints with this query. You can still save it,
                but it may not work as expected.
              </>
            ) : (
              errorMessage
            )}
          </div>
        )}
        {selectedHint.name && (
          <div className="grid gap-2">
            <div className="italic">{selectedHintDetail.description}</div>
            {selectedHintDetail.group === "Scan method" && (
              <div>
                <div>
                  Scan method hints enforce specific scanning methods on the
                  target table. pg_hint_plan recognizes the target table by
                  alias names if any.
                </div>
                <PlannerHintExample
                  sql={`/*+ SeqScan(t1) IndexScan(t2 t2_pkey) */
                    SELECT * FROM table1 t1 JOIN table table2 t2 ON (t1.key = t2.key);`}
                />
              </div>
            )}
            {["Join method", "Join order"].includes(
              selectedHintDetail.group,
            ) && (
              <div>
                <PlannerHintExample
                  sql={`/*+ NestLoop(t1 t2) MergeJoin(t1 t2 t3) Leading(t1 t2 t3) */
                    SELECT * FROM table1 t1
                      JOIN table table2 t2 ON (t1.key = t2.key)
                      JOIN table table3 t3 ON (t2.key = t3.key);`}
                />
              </div>
            )}
            {selectedHintDetail.group === "Row number correction" && (
              <div>
                <div>
                  <PlannerHintExample
                    sql={`/*+ Rows(a b #10) */ SELECT... ; Sets rows of join result to 10
                        /*+ Rows(a b +10) */ SELECT... ; Increments row number by 10
                        /*+ Rows(a b -10) */ SELECT... ; Subtracts 10 from the row number
                        /*+ Rows(a b *10) */ SELECT... ; Makes the number 10 times larger`}
                  />
                </div>
              </div>
            )}
            {selectedHintDetail.group === "Parallel query configuration" && (
              <div>
                <PlannerHintExample sql="/*+ Parallel(tl 5 hard) */ SELECT sum(a) FROM tl;" />
              </div>
            )}
            {selectedHintDetail.group === "GUC" && (
              <div>
                <div>
                  GUC parameter shown in Query Planning can have the expected
                  effects on planning unless an other hint conflicts with the
                  planner method configuration parameters. When multiple hints
                  change the same GUC, the last hint takes effect.
                </div>
                <PlannerHintExample
                  sql={`/*+ Set(random_page_cost 2.0) */
                    SELECT * FROM table1 t1 WHERE key = 'value';`}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

const PlannerHintExample = ({ sql }: { sql: string }) => {
  return (
    <div className="mt-2">
      <div className="text-sm text-gray-600 font-semibold mb-1">Example</div>
      <SQL className="rounded p-2 text-gray-600 !bg-[#F9F9F9]" sql={sql} />
    </div>
  );
};

export default PlannerHintsPanel;
